import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { ClipboardList } from 'lucide-react';
import { toggleFinding, normalizeFindings } from '@/utils/reportRules';

const samplerConfig = {
  symptoms: {
    title: 'Symptome',
    options: [
      { id: 'chest_pain', label: 'Thoraxschmerzen' },
      { id: 'dyspnea', label: 'Dyspnoe' },
      { id: 'nausea', label: 'Übelkeit' },
      { id: 'dizziness', label: 'Schwindel' },
      { id: 'pain', label: 'Schmerz' },
      { id: 'psychological_distress', label: 'Psychische Ausnahmesituation' },
      { id: 'weakness', label: 'Schwäche' },
      { id: 'confusion', label: 'Verwirrtheit' }
    ]
  },
  allergies: {
    title: 'Allergien',
    options: [
      { id: 'known', label: 'Bekannte Allergien vorhanden' },
      { id: 'none', label: 'Keine Allergien bekannt' },
      { id: 'unknown', label: 'Unbekannt (Patient nicht ansprechbar/bewusstlos)' }
    ]
  },
  medications: {
    title: 'Medikamente',
    options: [
      { id: 'as_prescribed', label: 'Nach Plan eingenommen' },
      { id: 'none', label: 'Keine Dauermedikation' },
      { id: 'irregular', label: 'Unregelmäßige Einnahme' },
      { id: 'unknown', label: 'Unbekannt (Patient nicht ansprechbar/bewusstlos)' }
    ]
  },
  history: {
    title: 'Patientenvorgeschichte',
    options: [
      { id: 'diabetes', label: 'Diabetes mellitus' },
      { id: 'chd', label: 'KHK (Koronare Herzkrankheit)' },
      { id: 'copd', label: 'COPD' },
      { id: 'asthma', label: 'Asthma bronchiale' },
      { id: 'hypertension', label: 'Arterielle Hypertonie' },
      { id: 'heart_failure', label: 'Herzinsuffizienz' },
      { id: 'stroke', label: 'Zustand nach Schlaganfall' },
      { id: 'atrial_fibrillation', label: 'Vorhofflimmern' },
      { id: 'renal_insufficiency', label: 'Niereninsuffizienz' },
      { id: 'epilepsy', label: 'Epilepsie' },
      { id: 'dementia', label: 'Demenz' },
      { id: 'cancer', label: 'Tumorerkrankung' },
      { id: 'depression', label: 'Depression' },
      { id: 'thyroid', label: 'Schilddrüsenerkrankung' },
      { id: 'liver_disease', label: 'Lebererkrankung' },
      { id: 'rheumatism', label: 'Rheumatische Erkrankung' },
      { id: 'none', label: 'Keine bekannten Vorerkrankungen' }
    ]
  },
  event: {
    title: 'Ereignis',
    options: [
      { id: 'fall', label: 'Sturz' },
      { id: 'chest_pain', label: 'Thoraxschmerz' },
      { id: 'dyspnea', label: 'Dyspnoe' },
      { id: 'dizziness', label: 'Schwindel' },
      { id: 'collapse', label: 'Synkope' },
      { id: 'trauma', label: 'Trauma' },
      { id: 'stroke_suspected', label: 'Verdacht auf Schlaganfall' },
      { id: 'seizure', label: 'Krampfanfall' },
      { id: 'accident', label: 'Verkehrsunfall' }
    ]
  },
  riskFactors: {
    title: 'Risikofaktoren',
    options: [
      { id: 'nicotine', label: 'Nikotinabusus' },
      { id: 'alcohol', label: 'Alkoholkonsum' },
      { id: 'obesity', label: 'Adipositas' },
      { id: 'drug_abuse', label: 'Drogenkonsum' },
      { id: 'sedentary', label: 'Bewegungsmangel' },
      { id: 'stress', label: 'Chronischer Stress' },
      { id: 'none', label: 'Keine bekannten Risikofaktoren' }
    ]
  }
};

export const SAMPLERSection = ({ data, onChange }) => {
  const handleToggle = (category, optionId) => {
    const currentSelections = data[category] || [];
    const newSelections = toggleFinding(normalizeFindings(currentSelections, category), optionId, category);
    
    onChange({
      ...data,
      [category]: newSelections
    });
  };

  const handleInputChange = (field, value) => {
    onChange({
      ...data,
      [field]: value
    });
  };

  const handleFreeTextChange = (category, value) => {
    onChange({
      ...data,
      [`${category}_freetext`]: value
    });
  };

  const weight = data.weight || 75;

  return (
    <Card className="section-card">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-success/10 rounded-lg">
            <ClipboardList className="w-5 h-5 text-[hsl(var(--success))]" />
          </div>
          <div>
            <CardTitle className="text-2xl">SAMPLER-Schema</CardTitle>
            <CardDescription className="text-base mt-1">
              Anamnese und Vorgeschichte
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {Object.entries(samplerConfig).map(([key, config]) => (
          <div key={key} className="space-y-3">
            <h3 className="text-lg font-semibold text-foreground border-b border-border pb-2">
              {config.title}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {config.options.map((option) => (
                <div
                  key={option.id}
                  data-testid={`sampler-${key}-${option.id.replaceAll('_', '-')}-option`}
                  className={`flex items-start space-x-3 p-3 rounded-lg border-2 transition-all cursor-pointer hover:shadow-sm ${
                    normalizeFindings(data[key], key).includes(option.id)
                      ? 'option-selected'
                      : 'border-border bg-card'
                  }`}
                  onClick={() => handleToggle(key, option.id)}
                >
                  <Checkbox
                    id={`${key}-${option.id}`}
                    data-testid={`sampler-${key}-${option.id.replaceAll('_', '-')}-checkbox`}
                    onClick={event => event.stopPropagation()}
                    checked={normalizeFindings(data[key], key).includes(option.id)}
                    onCheckedChange={() => handleToggle(key, option.id)}
                    className="mt-0.5"
                  />
                  <Label
                    htmlFor={`${key}-${option.id}`}
                    onClick={event => event.stopPropagation()}
                    className="text-sm font-medium cursor-pointer leading-relaxed flex-1"
                  >
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>
            {/* Freitextfeld für jeden Abschnitt */}
            <div className="pt-2">
              <Label htmlFor={`${key}-freetext`} className="text-xs font-medium text-muted-foreground">
                Zusätzliche Informationen
              </Label>
              <Textarea
                id={`${key}-freetext`}
                data-testid={`sampler-${key}-freetext`}
                placeholder="Weitere Details..."
                value={data[`${key}_freetext`] || ''}
                onChange={(e) => handleFreeTextChange(key, e.target.value)}
                className="mt-1 min-h-[60px] resize-y"
              />
            </div>
          </div>
        ))}

        {/* Last Meal and Weight Inputs */}
        <div className="space-y-4 pt-4 border-t border-border">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-foreground">Letzte Mahlzeit</h3>
              <Label htmlFor="lastMeal" className="text-sm font-medium">
                Zeitangabe
              </Label>
              <Input
                id="lastMeal"
                data-testid="sampler-last-meal-input"
                type="text"
                placeholder="z.B. vor 3 Stunden, heute Morgen"
                value={data.lastMeal || ''}
                onChange={(e) => handleInputChange('lastMeal', e.target.value)}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-foreground">Patientengewicht</h3>
              <div className="flex items-center justify-between mb-1">
                <Label htmlFor="weight" className="text-sm font-medium">
                  Gewicht in kg
                </Label>
                <span className="text-sm font-semibold text-primary">{weight} kg</span>
              </div>
              <Slider
                id="weight-slider"
                data-testid="sampler-weight-slider"
                min={20}
                max={200}
                step={1}
                value={[parseFloat(weight)]}
                onValueChange={(vals) => handleInputChange('weight', vals[0])}
                className="w-full mb-2"
              />
              <Input
                id="weight"
                data-testid="sampler-weight-input"
                type="number"
                placeholder="75"
                value={weight}
                onChange={(e) => handleInputChange('weight', e.target.value)}
                className="h-11"
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SAMPLERSection;
